import React from 'react';
import PrivacyPolicy from '../../components/PrivacyPolicy/PrivacyPolicy';

function AutoPersonasPrivacy() {
  const sections = [
    {
      title: 'Introduction',
      content: (
        <>
          <p>
            AutoPersonas is an AI-powered social media automation platform that enables users to
            create, manage, and deploy AI-generated virtual characters across multiple social media
            platforms. This Privacy Policy explains how Castle Point Innovations LLC ("we," "us," or
            "our") collects, uses, stores, and protects your information when you use the AutoPersonas
            application and its related services.
          </p>
          <p>
            By using AutoPersonas, you agree to the collection and use of information as described in
            this policy. If you do not agree with any part of this policy, please discontinue use of
            the application.
          </p>
        </>
      ),
    },
    {
      title: 'Information We Collect',
      content: (
        <>
          <p>We collect the following types of information when you use AutoPersonas:</p>
          <ul>
            <li>
              <strong>Account Information:</strong> When you create an account, we collect your email
              address and authentication credentials through Firebase Authentication. This information
              is required to provide you with secure access to the platform.
            </li>
            <li>
              <strong>Character Data:</strong> We collect metadata related to the virtual characters
              you create, including character names, descriptions, personality traits, persona
              configurations, and associated images uploaded to Cloud Storage.
            </li>
            <li>
              <strong>Social Media Credentials:</strong> If you connect social media accounts (such as
              Twitter/X), we collect and store the API keys, access tokens, and related credentials
              necessary to post content on your behalf.
            </li>
            <li>
              <strong>Generated Content:</strong> We store AI-generated posts, responses, and other
              content created by and for your virtual characters, including drafts, scheduled posts,
              and published content.
            </li>
            <li>
              <strong>Usage Data:</strong> We collect metrics related to your use of the platform,
              including feature usage, character activity, post frequency, and general interaction
              patterns to improve our services.
            </li>
            <li>
              <strong>Payment Information:</strong> If you subscribe to a paid plan, payment
              processing is handled by Stripe. We do not directly store your full credit card number
              or banking details.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      content: (
        <>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li>To create and manage your account and authenticate your identity.</li>
            <li>To generate, schedule, and publish AI-driven content on your behalf across connected social media platforms.</li>
            <li>To store and manage your virtual character profiles, personas, and associated media.</li>
            <li>To process payments and manage subscriptions through Stripe.</li>
            <li>To analyze usage patterns and improve the functionality and performance of AutoPersonas.</li>
            <li>To communicate with you regarding account updates, service changes, or support inquiries.</li>
            <li>To enforce our terms of service and protect against misuse of the platform.</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Third-Party Services',
      content: (
        <>
          <p>
            AutoPersonas integrates with the following third-party services, each of which operates
            under its own privacy policy:
          </p>
          <ul>
            <li>
              <strong>Firebase (Google):</strong> We use Firebase Authentication for user sign-in,
              Cloud Firestore for storing user data and character metadata, and Cloud Storage for
              hosting character images and content assets. Data is processed in accordance with
              Google's privacy policies.
            </li>
            <li>
              <strong>Google Generative AI (Gemini API):</strong> We use Google's Gemini API to
              generate character content, posts, and responses. Text prompts and character data may be
              sent to Google's servers for AI processing.
            </li>
            <li>
              <strong>Twitter API v2:</strong> We use the Twitter/X API to publish posts and manage
              social media activity for your virtual characters. Your connected Twitter account data is
              transmitted to Twitter in accordance with their developer terms and privacy policy.
            </li>
            <li>
              <strong>Stripe:</strong> We use Stripe to process subscription payments. Stripe collects
              and processes your payment information directly under its own privacy policy. We receive
              only limited transaction details necessary to manage your subscription.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Data Storage and Security',
      content: (
        <>
          <p>
            Your data is stored using Google Firebase services, including Cloud Firestore and Cloud
            Storage, which provide encryption at rest and in transit. Social media credentials are
            stored securely and are accessible only to authorized services within the application.
          </p>
          <p>
            We implement reasonable technical and organizational measures to protect your data from
            unauthorized access, alteration, disclosure, or destruction. However, no method of
            electronic storage or transmission is completely secure, and we cannot guarantee absolute
            security.
          </p>
        </>
      ),
    },
    {
      title: 'Data Retention',
      content: (
        <>
          <p>
            We retain your account information, character data, and generated content for as long as
            your account is active or as needed to provide you with our services. If you delete your
            account, we will remove your personal data and character information from our active
            databases within 30 days. Some data may be retained in backups for a limited period
            thereafter.
          </p>
          <p>
            Usage metrics and anonymized analytics data may be retained indefinitely in aggregated
            form for the purpose of improving our services.
          </p>
        </>
      ),
    },
    {
      title: 'Your Rights',
      content: (
        <>
          <p>Depending on your jurisdiction, you may have the following rights regarding your data:</p>
          <ul>
            <li>
              <strong>Access:</strong> You may request a copy of the personal data we hold about you.
            </li>
            <li>
              <strong>Deletion:</strong> You may request that we delete your account and all
              associated personal data, character data, and generated content.
            </li>
            <li>
              <strong>Portability:</strong> You may request an export of your data in a commonly used,
              machine-readable format.
            </li>
            <li>
              <strong>Correction:</strong> You may request correction of any inaccurate personal data
              we hold about you.
            </li>
            <li>
              <strong>Revocation of Consent:</strong> You may disconnect linked social media accounts
              or revoke permissions at any time through your account settings.
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the information provided in the
            Contact Us section below.
          </p>
        </>
      ),
    },
    {
      title: "Children's Privacy",
      content: (
        <>
          <p>
            AutoPersonas is not intended for use by individuals under the age of 13. We do not
            knowingly collect personal information from children under 13. If we become aware that we
            have inadvertently collected data from a child under 13, we will take steps to delete that
            information promptly. If you believe a child under 13 has provided us with personal data,
            please contact us immediately.
          </p>
        </>
      ),
    },
    {
      title: 'Changes to This Policy',
      content: (
        <>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices,
            technologies, or legal requirements. When we make material changes, we will update the
            "Last Updated" date at the top of this policy and, where appropriate, notify you via email
            or through the application. We encourage you to review this policy periodically.
          </p>
        </>
      ),
    },
    {
      title: 'Contact Us',
      content: (
        <>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data
            practices, please contact us at:
          </p>
          <p>
            <strong>Email:</strong> contact@castlepointinnovations.com
          </p>
        </>
      ),
    },
  ];

  return <PrivacyPolicy productName="AutoPersonas" lastUpdated="March 27, 2026" sections={sections} />;
}

export default AutoPersonasPrivacy;
